/**
 * Runtime glue for the nesting bird: pose art, sizing constants, and one
 * persistent state per yard.
 *
 * State lives here rather than on the view so the bird keeps its position and
 * mood across Friends switches and trips to the map, exactly like the rabbit.
 */

import {
  createBirdState,
  type BirdPose,
  type BirdState,
  type BirdYardConfig,
} from '@/world-prototype/creatures/birdBehavior';
import { birdConfigForYard } from '@/world-prototype/creatures/birdYards';
import {
  PHYSICAL_SIZE_FEET,
  READABILITY,
} from '@/world-prototype/scale/worldScale';

/**
 * Poses sliced from `Bird-Poses.png` onto one canvas with a shared ground
 * baseline, so swapping `src` never shifts the bird's feet.
 *
 * PNG on purpose: these are alpha cutouts, and lossy WebP leaves halos on
 * cutouts (the same reason `resolveRuntimeArtUrl` skips characters).
 */
const BIRD_POSE_DIR = `${import.meta.env.BASE_URL}art/animals/bird/poses`;

export const BIRD_POSE_SRC: Readonly<Record<BirdPose, string>> = {
  idle: `${BIRD_POSE_DIR}/bird-idle.png`,
  watch: `${BIRD_POSE_DIR}/bird-watch.png`,
  peer: `${BIRD_POSE_DIR}/bird-peer.png`,
  walk: `${BIRD_POSE_DIR}/bird-walk.png`,
  hop: `${BIRD_POSE_DIR}/bird-hop.png`,
  peck: `${BIRD_POSE_DIR}/bird-peck.png`,
  charge: `${BIRD_POSE_DIR}/bird-charge.png`,
  defend: `${BIRD_POSE_DIR}/bird-defend.png`,
  sleep: `${BIRD_POSE_DIR}/bird-sleep.png`,
};

/** Native size of every bird pose canvas (they are all identical). */
export const BIRD_POSE_IMAGE = {
  imageWidth: 474,
  imageHeight: 371,
} as const;

/**
 * Real-world height the bird's pose canvas spans, in feet.
 *
 * The tallest pose (standing idle) fills 321 of the canvas's 371 px, so the
 * canvas is scaled taller than the bird itself. One number drives every pose.
 */
export const BIRD_CANVAS_FEET =
  PHYSICAL_SIZE_FEET.bird * READABILITY.bird * (371 / 321);

/** Kid-friendly minimum tap target, in CSS pixels. */
export const BIRD_HIT_MIN_CSS_PX = 76;

const states = new Map<string, BirdState>();

/**
 * Long-lived bird state for a yard, created on first request.
 *
 * Pass the resolved config (nest where the editor actually put it) so a
 * first-time bird is born beside its real nest instead of the authored one.
 */
export function birdStateForYard(
  yardId: string,
  config?: BirdYardConfig,
): BirdState | null {
  const resolved = config ?? birdConfigForYard(yardId);
  if (!resolved) return null;
  let state = states.get(yardId);
  if (!state) {
    state = createBirdState(resolved);
    states.set(yardId, state);
  }
  return state;
}

export function resetBirdStatesForTests(): void {
  states.clear();
}
